// @flow
import React, { Component } from 'react'
import StripeError from '../StripeError'
import shallowEqual from 'mz-utils/shallowEqual'
import { ProvidePalette } from '../../../utils/withPalette'
import { createField } from '../../Field'
import BaseField from '../../../core/BaseField'
import { StaticValueContainer } from '../../../core/Input/elements'

// Types
import type { Input, InputRenderProps, Meta } from '../../../core/BaseField'
import type { NestFunc } from '../../Field'
import type { StripeElement, ElementsObject } from '../stripe'
import type { PaletteColorGetter } from '../../../utils/withPalette'

export type Props = {
  input: {
    ...Input,
    onChange?: (?Value) => any,
  },
  meta: Meta,
  nest: NestFunc,
  onValidationError?: (errors: any) => any,
  name: string,
  elements: ElementsObject,
  palette: PaletteColorGetter,
  placeholder: string,
  label: string,
}

export type Value = {
  event: StripeEvent,
  element: ?StripeElement,
}

type StripeEvent = {
  complete: boolean,
  empty: boolean,
  brand: string,
  error?: { message: string, code: string },
}

type State = {
  element: ?StripeElement,
}

/**
 * Wrapper around Stripe fields which handle
 * focus, blur and change event on a mounted
 * Stripe element
 */
export class StripeField extends Component<Props, State> {
  fieldContainer: null

  state = { element: null }

  componentDidMount() {
    this.initStripeField(this.props)
  }

  componentWillUnmount() {
    if (this.state.element) {
      this.state.element.destroy()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const shouldReinitField = !shallowEqual(nextProps, this.props, {
      include: {
        elements: true,
        name: true,
      },
    })

    if (shouldReinitField) {
      this.initStripeField(nextProps)
    }
  }

  initStripeField(props: Props) {
    const { name, elements, palette } = props
    const stripeElement = elements.create(name, {
      style: {
        base: {
          color: `${palette('text')}`,
          fontWeight: 400,
          fontFamily: 'Roboto, Helvetica Neue, Helvetica, Arial, sans-serif',
          fontSize: '14px',
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: `${palette('text').lighten(35)}`,
          },
          ':-webkit-autofill': {
            color: `${palette('text')}`,
          },
          '::-ms-clear': {
            display: 'none',
          },
        },
        invalid: {
          color: `${palette('red')}`,
        },
      },
    })

    stripeElement.on('focus', this.handleFocus)
    stripeElement.on('blur', this.handleBlur)
    stripeElement.on('change', this.handleChange)
    stripeElement.mount(this.fieldContainer)

    this.setState({ element: stripeElement })
  }

  setFieldRef = (node: any) => {
    this.fieldContainer = node
  }

  handleBlur = () => {
    const {
      input: { onBlur },
    } = this.props
    onBlur && onBlur()
  }

  handleFocus = () => {
    const {
      input: { onFocus },
    } = this.props
    onFocus && onFocus()
  }

  handleChange = (event: StripeEvent) => {
    const { element } = this.state
    const {
      input: { onChange },
      onValidationError,
    } = this.props

    if (event.error) {
      const { error } = event
      const stripeError = new StripeError(error.code, null, error.message)
      const errorMessage = stripeError.i18n

      onValidationError && onValidationError(errorMessage)
    } else {
      onValidationError && onValidationError(null)
    }

    onChange && onChange({ element, event })
  }

  renderInput = ({ style }: InputRenderProps) => (
    <StaticValueContainer {...style}>
      <div ref={this.setFieldRef} />
    </StaticValueContainer>
  )

  render() {
    const { placeholder, label, meta } = this.props

    return (
      <BaseField
        {...this.props}
        meta={meta}
        label={label}
        placeholder={placeholder}
        renderInput={this.renderInput}
      />
    )
  }
}

export default createField(
  (props, nest, options, controller) => (
    <ProvidePalette
      render={(palette) => (
        <StripeField
          {...props}
          {...options}
          nest={nest}
          palette={palette}
          onValidationError={controller.handleValidationResult}
        />
      )}
    />
  ),
  {
    validate: (val, opts, controller) =>
      // We set a validation error using `handleValidationResult` method
      // of the controller right in the component `handleChange` method.
      // But we need to keep the error when value of the field changed, so
      // only another call to `handleValidationResult` will be able to remove
      // the error.
      controller.meta.error,
  }
)
